import { AxiosESTransport, SearchkitManager } from 'searchkit';
import { startOver } from './helpers';
import { getSearchAuthHeaders, isMarketplaceApiSearchRoot } from './searchAuth';

/**
 * Searchkit transport that authenticates to marketplace-api's search endpoint.
 *
 * The credential is read when each search is sent, not when the transport is built:
 * `SearchkitManager` instances are created before sign-in (MarketplaceSearch builds one
 * at module load), and the key is refreshed as it expires. Searchkit's own `httpHeaders`
 * option is fixed at construction, so it cannot do this.
 *
 * Against the old Elasticsearch host it behaves exactly like searchkit's default transport.
 */
class MarketplaceSearchTransport extends AxiosESTransport {
  async search(query) {
    if (!isMarketplaceApiSearchRoot(this.host)) {
      return super.search(query);
    }

    const send = headers =>
      this.axios.post(this.options.searchUrlPath, query, { headers });

    const headers = await getSearchAuthHeaders();
    if (!headers) {
      startOver();
      throw new Error('Search requires a signed-in user.');
    }

    try {
      return this.getData(await send(headers));
    } catch (error) {
      if (error?.response?.status !== 401) throw error;

      // The server ended the session before the stored key's own expiry. Refresh once.
      const refreshed = await getSearchAuthHeaders({ forceRefresh: true });
      if (!refreshed) {
        startOver();
        throw error;
      }

      return this.getData(await send(refreshed));
    }
  }
}

/**
 * `new SearchkitManager(searchRoot, options)`, with a transport that authenticates to
 * marketplace-api when `searchRoot` points there. Use this for every search page.
 *
 * @param {string} searchRoot
 * @param {Object} [options] SearchkitManager options
 * @returns {SearchkitManager}
 */
export const createSearchkitManager = (searchRoot, options = {}) =>
  new SearchkitManager(searchRoot, {
    ...options,
    transport: new MarketplaceSearchTransport(searchRoot),
  });
